import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Star, Truck, RotateCcw, ShieldCheck, Sparkles, ChevronDown, ArrowRight, Check } from "lucide-react";
import {
  HERO, TRUST_BADGES, FEATURE_BENEFITS, REVEAL_BENEFITS, MODERN_LIVING,
  PRESS_LOGOS, FAQS, LIFESTYLE, PRODUCT, HERO_BULLETS, ABOUT, VC_BANNER, HERO_VIDEO,
} from "../mock";
import { useCart } from "../context/CartContext";
import GoogleG from "../components/GoogleG";
import LuxuryReviews from "../components/LuxuryReviews";
import VideoSlider from "../components/VideoSlider";

const Stars = ({ size = 14 }) => (
  <div className="flex gap-0.5 text-[#1A1A1A]">
    {[...Array(5)].map((_, i) => (
      <Star key={i} size={size} fill="currentColor" strokeWidth={0} />
    ))}
  </div>
);

const badgeIcons = [Sparkles, Truck, RotateCcw, ShieldCheck];

const Home = () => {
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const [openFaq, setOpenFaq] = useState(0);
  const goProduct = () => navigate(`/products/${PRODUCT.handle}`);

  return (
    <main>
      {/* HERO */}
      <section className="relative overflow-hidden bg-[#EDE6DC] min-h-[560px] md:min-h-[660px] flex items-center">
        {/* Background video */}
        <video
          className="absolute inset-0 w-full h-full object-cover"
          autoPlay
          loop
          muted
          playsInline
          poster={HERO.image}
        >
          <source src={HERO_VIDEO} type="video/mp4" />
        </video>
        {/* Readability scrim */}
        <div className="absolute inset-0 bg-gradient-to-r from-[#F4EFE8]/95 via-[#F4EFE8]/78 to-[#F4EFE8]/35 md:to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#F4EFE8]/40 to-transparent" />

        {/* Price badge (top-right) */}
        <div className="absolute top-24 right-5 md:top-28 md:right-10 z-20">
          <div className="h-28 w-28 md:h-36 md:w-36 rounded-full bg-[#6D28D9]/30 backdrop-blur-md border border-white/50 shadow-[0_10px_40px_-12px_rgba(109,40,217,0.6)] flex flex-col items-center justify-center text-center text-white px-3 animate-rise">
            <span className="font-serif-display text-[26px] md:text-[34px] leading-none font-bold">$115</span>
            <span className="mt-1 text-[9px] md:text-[10px] tracking-[0.16em] uppercase font-semibold leading-tight">
              + Free<br />Shipping
            </span>
          </div>
        </div>

        {/* Content */}
        <div className="relative w-full max-w-[1280px] mx-auto px-5 md:px-8 py-16 md:py-24">
          <div className="animate-rise max-w-xl">
            <p className="text-[12px] tracking-[0.32em] uppercase text-[#8a6d3b] mb-5">{HERO.eyebrow}</p>
            <div className="inline-flex items-center gap-2 mb-5 bg-[#6D28D9]/15 text-[#6D28D9] px-3 py-1.5 rounded-full text-[11px] tracking-[0.14em] uppercase font-semibold backdrop-blur-sm">
              <span className="h-1.5 w-1.5 rounded-full bg-[#6D28D9]" />
              VC-Backed &bull; Built by 4 Dermatologists
            </div>
            <h1 className="font-serif-display text-[46px] leading-[1.02] md:text-[72px] md:leading-[0.98] font-medium">
              {HERO.titleTop}
              <br />
              <span className="italic">{HERO.titleBottom}</span>
            </h1>
            <p className="mt-6 text-[15px] leading-relaxed text-[#1A1A1A]/80 max-w-md">
              {HERO.subtitle}
            </p>

            {/* Purple guarantee bullets */}
            <ul className="mt-7 flex flex-col sm:flex-row gap-x-8 gap-y-3">
              {HERO_BULLETS.map((b) => (
                <li key={b} className="flex items-center gap-2.5 text-[#6D28D9] font-bold text-[14px] tracking-wide">
                  <span className="h-3 w-3 rounded-full bg-[#6D28D9] shadow-[0_0_0_4px_rgba(109,40,217,0.15)]" />
                  {b}
                </li>
              ))}
            </ul>

            <div className="mt-8 flex flex-col sm:flex-row items-start sm:items-center gap-5">
              <button
                onClick={goProduct}
                className="btn-lux px-10 py-4 text-[12px] tracking-[0.22em] uppercase"
              >
                {HERO.cta}
              </button>
              <div className="flex items-center gap-3">
                <GoogleG size={30} />
                <div className="flex flex-col">
                  <Stars />
                  <span className="text-[12px] mt-1 text-[#1A1A1A]/80">4.9/5 from 1,500 Google reviews</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PRESS MARQUEE */}
      <section className="py-10 bg-[#F4EFE8] border-y border-[#1A1A1A]/10 overflow-hidden">
        <p className="text-center text-[11px] tracking-[0.3em] uppercase text-[#1A1A1A]/50 mb-6">As Seen In</p>
        <div className="flex whitespace-nowrap animate-marquee items-center">
          {[...PRESS_LOGOS, ...PRESS_LOGOS, ...PRESS_LOGOS, ...PRESS_LOGOS].map((l, i) => (
            <img key={i} src={l} alt="press" className="h-8 md:h-10 w-auto mx-10 opacity-60 grayscale object-contain" />
          ))}
        </div>
      </section>

      {/* UGC VIDEO SLIDER */}
      <VideoSlider />

      {/* VC / DERMATOLOGIST BANNER */}
      <section className="bg-[#6D28D9] text-white overflow-hidden">
        <div className="flex whitespace-nowrap animate-marquee-fast text-[11px] tracking-[0.22em] uppercase font-semibold py-3">
          {[...Array(8)].map((_, i) => (
            <span key={i} className="px-8 inline-flex items-center">
              {VC_BANNER}
            </span>
          ))}
        </div>
      </section>

      {/* WHY FLOWLUX \u2014 DERMATOLOGISTS + VC */}
      <section className="relative overflow-hidden py-20 md:py-28 bg-[#F4EFE8]">
        {/* Faded background photo */}
        <div
          className="absolute inset-0 bg-center bg-no-repeat bg-contain opacity-[0.14] pointer-events-none"
          style={{ backgroundImage: `url(${ABOUT.bgImage})` }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#F4EFE8]/70 via-transparent to-[#F4EFE8]/70 pointer-events-none" />
        <div className="relative z-10 max-w-[1080px] mx-auto px-5 md:px-8 text-center">
          <p className="text-[12px] tracking-[0.3em] uppercase text-[#6D28D9] font-semibold mb-5">{ABOUT.eyebrow}</p>
          <h2 className="font-serif-display text-[36px] md:text-[54px] leading-[1.05] font-medium mb-7">
            {ABOUT.headline}
          </h2>
          <p className="text-[17px] md:text-[19px] leading-relaxed text-[#1A1A1A]/80 max-w-3xl mx-auto">
            {ABOUT.body}
          </p>

          <div className="mt-10 mx-auto max-w-3xl bg-white border border-[#6D28D9]/20 rounded-md px-7 py-6 shadow-[0_10px_40px_-20px_rgba(109,40,217,0.5)]">
            <p className="text-[15px] md:text-[16px] leading-relaxed text-[#1A1A1A]">
              <span className="text-[#6D28D9] font-bold">Backed by 5 VC investing companies</span> and developed by{" "}
              <span className="text-[#6D28D9] font-bold">4 dermatologists</span> to create the first affordable, custom red light therapy mask &mdash; so anyone can get affordable skincare the right way.
            </p>
          </div>

          <div className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-8">
            {ABOUT.stats.map((s) => (
              <div key={s.label} className="flex flex-col items-center">
                <span className="font-serif-display text-[52px] md:text-[64px] leading-none font-medium text-[#6D28D9]">{s.value}</span>
                <span className="text-[12px] tracking-[0.12em] uppercase text-[#1A1A1A]/60 mt-2">{s.label}</span>
              </div>
            ))}
          </div>

          <div className="mt-11">
            <button
              onClick={goProduct}
              className="btn-lux px-11 py-4 text-[12px] tracking-[0.22em] uppercase"
            >
              Shop the AI-Enhanced Mask
            </button>
          </div>
        </div>
      </section>

      {/* TRUST BADGES */}
      <section className="border-y border-[#1A1A1A]/10 bg-[#F4EFE8]">
        <div className="max-w-[1280px] mx-auto px-5 md:px-8 grid grid-cols-2 md:grid-cols-4 divide-x divide-[#1A1A1A]/10">
          {TRUST_BADGES.map((b, i) => {
            const Icon = badgeIcons[i];
            return (
              <div key={b.title} className="flex flex-col items-center text-center gap-2 py-8 px-4">
                <Icon size={22} strokeWidth={1.3} />
                <p className="text-[12px] tracking-[0.16em] uppercase font-medium">{b.title}</p>
                <p className="text-[12px] text-[#1A1A1A]/55 leading-snug">{b.desc}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* VISIBLE RESULTS */}
      <section className="py-20 md:py-28 bg-[#EDE6DC]">
        <div className="max-w-[1280px] mx-auto px-5 md:px-8 grid md:grid-cols-2 gap-12 items-center">
          <div className="relative">
            <img src={LIFESTYLE.silkMask} alt="Silk mask" className="w-full rounded-sm object-cover" />
            <div className="absolute top-5 left-5 bg-[#1A1A1A] text-[#F4EFE8] px-4 py-2 text-[10px] tracking-[0.18em] uppercase">
              Clinically Inspired
            </div>
          </div>
          <div>
            <p className="text-[12px] tracking-[0.3em] uppercase text-[#8a6d3b] mb-4">Dermatologist recommended</p>
            <h2 className="font-serif-display text-[38px] md:text-[52px] leading-[1.05] font-medium mb-5">
              Visible Results. <span className="italic">Confident You.</span>
            </h2>
            <p className="text-[15px] text-[#1A1A1A]/70 leading-relaxed mb-8 max-w-md">
              Customers see visible improvements in as little as 2 weeks with just 15 minutes of consistent daily use.
            </p>
            <div className="grid sm:grid-cols-2 gap-x-8 gap-y-7">
              {FEATURE_BENEFITS.map((f) => (
                <div key={f.title}>
                  <h4 className="text-[15px] font-medium mb-1.5">{f.title}</h4>
                  <p className="text-[13px] text-[#1A1A1A]/60 leading-relaxed">{f.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* LUXURY REVIEWS */}
      <LuxuryReviews />

      {/* REVEAL YOUR BEST SKIN */}
      <section className="py-20 md:py-28 bg-[#F4EFE8]">
        <div className="max-w-[1280px] mx-auto px-5 md:px-8 grid md:grid-cols-2 gap-12 items-center">
          <div className="order-2 md:order-1">
            <p className="text-[12px] tracking-[0.3em] uppercase text-[#8a6d3b] mb-4">Skip the filters. Glow naturally.</p>
            <h2 className="font-serif-display text-[38px] md:text-[52px] leading-[1.05] font-medium mb-5">
              Reveal Your <span className="italic">Best Skin</span>
            </h2>
            <p className="text-[15px] text-[#1A1A1A]/70 leading-relaxed mb-8 max-w-md">
              Experience the viral dermatologist-backed red light therapy designed to support clearer, firmer, and more radiant looking skin from the comfort of home.
            </p>
            <div className="space-y-6 mb-9">
              {REVEAL_BENEFITS.map((f) => (
                <div key={f.title} className="flex gap-4">
                  <div className="mt-0.5 h-6 w-6 rounded-full bg-[#1A1A1A] text-[#F4EFE8] flex items-center justify-center flex-shrink-0">
                    <Check size={13} />
                  </div>
                  <div>
                    <h4 className="text-[15px] font-medium">{f.title}</h4>
                    <p className="text-[13px] text-[#1A1A1A]/60 leading-relaxed">{f.desc}</p>
                  </div>
                </div>
              ))}
            </div>
            <button
              onClick={goProduct}
              className="group inline-flex items-center gap-2 text-[12px] tracking-[0.22em] uppercase border-b border-[#1A1A1A] pb-1 hover:gap-4 transition-all"
            >
              Get your glow up! <ArrowRight size={15} className="group-hover:translate-x-1 transition-transform" />
            </button>
          </div>
          <div className="order-1 md:order-2">
            <img src={LIFESTYLE.girlMask} alt="Reveal your best skin" className="w-full rounded-sm object-cover" />
          </div>
        </div>
      </section>

      {/* 15 MIN GLOW UP */}
      <section className="py-20 md:py-28 bg-[#EDE6DC]">
        <div className="max-w-[1280px] mx-auto px-5 md:px-8 grid md:grid-cols-2 gap-12 items-center">
          <div>
            <p className="text-[12px] tracking-[0.3em] uppercase text-[#8a6d3b] mb-4">You. Deserve. Better Skin.</p>
            <h2 className="font-serif-display text-[40px] md:text-[56px] leading-[1.02] font-medium mb-4">
              A Quick 15 Mins <span className="italic">&rarr; Big Glow Up</span>
            </h2>
            <p className="text-[16px] text-[#1A1A1A]/70 mb-9">Yes, it's THAT simple!</p>
            <div className="flex flex-wrap gap-4">
              <button onClick={goProduct} className="btn-lux px-9 py-4 text-[12px] tracking-[0.22em] uppercase gap-2">
                Get your mask! <ArrowRight size={15} />
              </button>
              <button onClick={() => navigate("/blog")} className="btn-lux px-9 py-4 text-[12px] tracking-[0.22em] uppercase gap-2">
                Explore journal <ArrowRight size={15} />
              </button>
            </div>
          </div>
          <img src={LIFESTYLE.sleeping} alt="Nightly routine" className="w-full rounded-sm object-cover" />
        </div>
      </section>

      {/* MODERN LIVING STRIP */}
      <section className="py-16 bg-[#F4EFE8] border-y border-[#1A1A1A]/10">
        <div className="max-w-[1280px] mx-auto px-5 md:px-8 grid grid-cols-2 md:grid-cols-4 gap-8">
          {MODERN_LIVING.map((m) => (
            <div key={m.title} className="text-center">
              <p className="text-[12px] tracking-[0.2em] uppercase font-medium mb-2">{m.title}</p>
              <p className="text-[13px] text-[#1A1A1A]/55">{m.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* SHOP WITH CONFIDENCE */}
      <section className="relative py-24 md:py-32 bg-[#EDE6DC]">
        <div className="max-w-2xl mx-auto px-6 text-center">
          <h2 className="font-serif-display text-[40px] md:text-[52px] leading-tight font-medium mb-5">
            Shop with Total Confidence
          </h2>
          <p className="text-[15px] text-[#1A1A1A]/70 mb-9">
            Red Light Therapy is clinically backed by Dermatologists in the U.S.!
          </p>
          <div className="inline-flex flex-col gap-3 text-left mb-10">
            {["Fast & Free U.S. Shipping", "30 Day Money Back Guarantee!", "Complete Glow Up with Every Mask"].map((t) => (
              <div key={t} className="flex items-center gap-3">
                <Check size={17} className="text-[#8a6d3b]" />
                <span className="text-[15px]">{t}</span>
              </div>
            ))}
          </div>
          <div>
            <button onClick={goProduct} className="btn-lux px-12 py-4 text-[12px] tracking-[0.22em] uppercase">
              Shop Now
            </button>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="py-20 md:py-28 bg-[#F4EFE8]">
        <div className="max-w-3xl mx-auto px-5 md:px-8">
          <div className="flex items-end justify-between mb-10">
            <h2 className="font-serif-display text-[34px] md:text-[46px] font-medium">Frequently Asked Questions</h2>
          </div>
          <div className="divide-y divide-[#1A1A1A]/12 border-y border-[#1A1A1A]/12">
            {FAQS.map((f, i) => (
              <div key={i}>
                <button
                  onClick={() => setOpenFaq(openFaq === i ? -1 : i)}
                  className="w-full flex items-center justify-between text-left py-5 gap-4"
                >
                  <span className="text-[16px] md:text-[18px] font-serif-display font-medium">{f.q}</span>
                  <ChevronDown
                    size={20}
                    className={`flex-shrink-0 transition-transform duration-300 ${openFaq === i ? "rotate-180" : ""}`}
                  />
                </button>
                <div
                  className={`overflow-hidden transition-all duration-300 ${openFaq === i ? "max-h-60 pb-5" : "max-h-0"}`}
                >
                  <p className="text-[14px] text-[#1A1A1A]/65 leading-relaxed">{f.a}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
};

export default Home;
